import _ from 'underscore';

import { weightedAverage, weightedAverageByPeriod } from 'views/benchmark-analytics/utils/benchmark-math';
import { BenchmarkTimeGrouping } from 'views/benchmark-analytics/types/benchmark-filters.type';
import { GrowthBenchmark } from '../types/growth-benchmark.type';
import { GrowthPoint } from '../types/growth-series.type';

// How the Growth page calculates its numbers
//
// Each point is one day: the average growth (g/day) of the shrimp harvested that day,
// and how many shrimp were harvested (totalOutQuantity).
//
// Averages are weighted by shrimp harvested, so a big harvest day counts more than a small one:
//   day A: 1,000 shrimp at 0.30 g/day
//   day B: 3,000 shrimp at 0.50 g/day
//   weighted average = (1,000 × 0.30 + 3,000 × 0.50) / 4,000 = 0.45, where a plain average says 0.40
//
// Period values (week, month, quarter) use the same formula over that period's days.
// The overall cards use it over every day in the range at once, never an average of the period values.

// A day counts when it has a real growth number and at least one shrimp harvested. Negative growth still counts.
const toWeightedDays = (points: GrowthPoint[]) =>
  points
    .filter((point) => Number.isFinite(point.growthGramPerDay) && point.totalOutQuantity > 0)
    .map((point) => ({ date: point.date, value: point.growthGramPerDay, weight: point.totalOutQuantity }));

const difference = (a: number | null, b: number | null): number | null => (a === null || b === null ? null : a - b);

// Every number the Growth page shows: chart lines, table cells and KPI cards.
export const buildGrowthBenchmark = (
  points: { company: GrowthPoint[]; market: GrowthPoint[]; farms: GrowthPoint[][] },
  grouping: BenchmarkTimeGrouping
): GrowthBenchmark => {
  const companyDays = toWeightedDays(points.company);
  const marketDays = toWeightedDays(points.market);

  // Company and market decide which periods exist. Farms only fill in values, and a missing period stays null, never 0.
  const companyByPeriod = weightedAverageByPeriod(companyDays, grouping);
  const marketByPeriod = weightedAverageByPeriod(marketDays, grouping);
  const periods = _.union(Object.keys(companyByPeriod), Object.keys(marketByPeriod)).sort();

  // Turns { '2026-03': 0.51 } into one value per period, in period order.
  const onPeriods = (valueByPeriod: Record<string, number | null>) => periods.map((period) => valueByPeriod[period] ?? null);
  const company = onPeriods(companyByPeriod);
  const market = onPeriods(marketByPeriod);

  const overallCompany = weightedAverage(companyDays);
  const overallMarket = weightedAverage(marketDays);

  return {
    periods,
    company,
    market,
    delta: periods.map((_period, index) => difference(company[index], market[index])),
    farms: points.farms.map((farmPoints) => onPeriods(weightedAverageByPeriod(toWeightedDays(farmPoints), grouping))),
    overall: {
      company: overallCompany,
      market: overallMarket,
      delta: difference(overallCompany, overallMarket),
      matchedPeriods: periods.filter((_period, index) => company[index] !== null && market[index] !== null).length,
      companyRows: companyDays.length,
      marketRows: marketDays.length,
    },
  };
};
